import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { getSiteThemes } from "@/lib/api";
import { post } from "@rails/request.js";
import { useParams, Link } from "react-router-dom";

const SiteSettings: React.FC = () => {
  const { siteSlug } = useParams<{ siteSlug: string }>();
  const siteId = siteSlug;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["site-themes", siteId],
    queryFn: () => getSiteThemes(siteId),
    enabled: !!siteId,
  });

  // Dialog state for new theme
  const [open, setOpen] = React.useState(false);
  const [themeForm, setThemeForm] = React.useState({ name: "" });

  const createThemeMutation = useMutation({
    mutationFn: async (params: { name: string }) => {
      const response = await post(`/sites/${siteId}/themes.json`, {
        body: JSON.stringify({ theme: params }),
        responseKind: "json",
      });
      if (!response.ok) {
        const error = await response.json;
        throw new Error(error.errors ? error.errors.join(", ") : "Failed to create theme");
      }
      return response.json;
    },
    onSuccess: () => {
      setOpen(false);
      setThemeForm({ name: "" });
      queryClient.invalidateQueries({ queryKey: ["site-themes", siteId] });
    },
  });

  return (
    <div className="max-w-5xl- mx-auto- px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">your themes</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="ml-auto">Create new theme</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create new theme</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={e => {
                e.preventDefault();
                createThemeMutation.mutate(themeForm);
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="theme-name">Theme name</Label>
                <Input
                  id="theme-name"
                  name="name"
                  value={themeForm.name}
                  onChange={e => setThemeForm({ ...themeForm, name: e.target.value })}
                  placeholder="Enter theme name"
                  required
                />
              </div>
              {createThemeMutation.isError && (
                <Alert variant="destructive" className="mb-2">
                  {(createThemeMutation.error as Error).message}
                </Alert>
              )}
              <DialogFooter>
                <Button type="submit" disabled={createThemeMutation.isPending}>
                  {createThemeMutation.isPending ? "Creating..." : "Create Theme"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {isLoading ? (
        <div className="text-muted-foreground">Loading themes...</div>
      ) : isError ? (
        <Alert variant="destructive">{(error as Error).message}</Alert>
      ) : (
        <>
          <div className="mb-8">
            <div className="text-lg font-semibold mb-2">Activated</div>
            {data.activated_in_site ? (
              <Card className="p-0 mb-2 border-2 border-black">
                <div className="flex flex-col h-full">
                  {
                    data.activated_in_site.image_url &&
                    <img src={data.activated_in_site.image_url} alt={data.activated_in_site.name} className="object-contain w-full h-32 bg-white border-b" />

                  }
                  <div className="p-2">
                    <div className="font-semibold">{data.activated_in_site.name}</div>
                    <div className="bg-yellow-200 text-yellow-900 text-xs rounded px-2 py-1 my-2">
                      This theme is used in {data.site?.name || "this site"}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          Manage ▼
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <Link
                          to={`/sites/${siteSlug}/themes/${data.activated_in_site.id}/edit`}
                          className="block w-full text-left px-2 py-1 hover:bg-muted/50 transition-colors"
                        >
                          Edit
                        </Link>
                        <DropdownMenuItem>Duplicate</DropdownMenuItem>
                        <DropdownMenuItem>Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </Card>
            ) : (
              <div className="text-muted-foreground">No activated theme.</div>
            )}
          </div>
          <div>
            <div className="text-lg font-semibold mb-2">Available themes</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {data.themes && data.themes.length > 0 ? (
                data.themes.map((theme: any) => (
                  <Card key={theme.id} className="p-0">
                    <div className="flex flex-col h-full">
                      {theme.image_url && <img src={theme.image_url} alt={theme.name} className="object-contain w-full h-32 bg-white border-b" />}
                      <div className="p-2">
                        <div className="font-semibold">{theme.name}</div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">Manage ▼</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <Link
                              to={`/sites/${siteSlug}/themes/${theme.id}/edit`}
                              className="block w-full text-left px-2 py-1 hover:bg-muted/50 transition-colors"
                            >
                              Edit
                            </Link>
                            <DropdownMenuItem>Duplicate</DropdownMenuItem>
                            <DropdownMenuItem>Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </Card>
                ))
              ) : (
                <div className="text-muted-foreground">No available themes.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SiteSettings;
